import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthProvider'
import { supabase } from '../../lib/supabase'
import type { DatasetKey } from '../../types'

type StandardDatasetKey = Extract<DatasetKey, 'qualificacoes' | 'acionamentos' | 'servicos' | 'irregularidades'>
type FieldType = 'text' | 'integer' | 'numeric' | 'array'

type FieldConfig = {
  key: string
  label: string
  type: FieldType
  required?: boolean
  multiline?: boolean
  placeholder?: string
}

type DatasetConfig = {
  key: StandardDatasetKey
  label: string
  singular: string
  description: string
  fields: FieldConfig[]
  tableColumns: string[]
  matchFields: string[]
  orderBy: string
  sample: Record<string, string>
}

type DraftRelease = {
  id: number
  version: string
  titulo: string
  status: 'rascunho'
}

type DraftChange = {
  id: number
  release_id: number
  operacao: 'INSERT' | 'UPDATE' | 'DELETE'
  record_key: string
  dados_anteriores: Record<string, unknown> | null
  dados_novos: Record<string, unknown> | null
  approval_status: 'rascunho' | 'aguardando_aprovacao' | 'aprovado' | 'rejeitado'
  rejection_reason: string | null
}

type FormState = Record<string, string>
type DataRecord = Record<string, unknown> & { id: number }

const CONFIGS: Record<StandardDatasetKey, DatasetConfig> = {
  qualificacoes: {
    key: 'qualificacoes',
    label: 'Qualificações',
    singular: 'qualificação',
    description: 'Padronização de classificações, frases e sinônimos operacionais.',
    fields: [
      { key: 'antigo', label: 'Termo antigo', type: 'text', required: true },
      { key: 'novo', label: 'Termo novo', type: 'text', required: true },
      { key: 'categoria', label: 'Categoria', type: 'text', required: true },
      { key: 'confianca', label: 'Confiança', type: 'text', required: true },
      { key: 'observacao', label: 'Observação', type: 'text', multiline: true },
      { key: 'frases', label: 'Frases', type: 'array', required: true, multiline: true, placeholder: 'Separe os itens por |' },
      { key: 'sinonimos', label: 'Sinônimos', type: 'array', required: true, multiline: true, placeholder: 'Separe os itens por |' },
    ],
    tableColumns: ['novo', 'antigo', 'categoria', 'confianca'],
    matchFields: ['categoria', 'novo'],
    orderBy: 'novo',
    sample: {
      antigo: 'Termo antigo',
      novo: 'Termo padronizado',
      categoria: 'Exemplo',
      confianca: 'Média',
      observacao: 'Observação opcional',
      frases: 'frase 1 | frase 2',
      sinonimos: 'sinônimo 1 | sinônimo 2',
    },
  },
  acionamentos: {
    key: 'acionamentos',
    label: 'Acionamentos',
    singular: 'acionamento',
    description: 'Responsáveis da Central de Serviços por setor, papel e contato.',
    fields: [
      { key: 'codigo', label: 'Código', type: 'text', required: true },
      { key: 'ordem_setor', label: 'Ordem do setor', type: 'integer', required: true },
      { key: 'setor', label: 'Setor', type: 'text', required: true },
      { key: 'papel', label: 'Papel', type: 'text', required: true },
      { key: 'nome', label: 'Nome', type: 'text', required: true },
      { key: 'email', label: 'E-mail', type: 'text' },
      { key: 'telefone', label: 'Telefone', type: 'text' },
    ],
    tableColumns: ['codigo', 'setor', 'papel', 'nome', 'telefone'],
    matchFields: ['codigo'],
    orderBy: 'ordem_setor',
    sample: {
      codigo: 'AC-999',
      ordem_setor: '999',
      setor: 'Setor Exemplo',
      papel: 'Responsável',
      nome: 'Nome do responsável',
      email: 'responsavel@empresa.com.br',
      telefone: '(67) 99999-9999',
    },
  },
  servicos: {
    key: 'servicos',
    label: 'Tabela de Serviços',
    singular: 'serviço',
    description: 'Serviços, categorias e tarifas vigentes.',
    fields: [
      { key: 'categoria', label: 'Categoria', type: 'text', required: true },
      { key: 'item', label: 'Item', type: 'text', required: true },
      { key: 'tarifa', label: 'Tarifa', type: 'text', required: true },
    ],
    tableColumns: ['categoria', 'item', 'tarifa'],
    matchFields: ['categoria', 'item'],
    orderBy: 'categoria',
    sample: {
      categoria: 'Categoria Exemplo',
      item: 'Serviço Exemplo',
      tarifa: 'R$ 0,00',
    },
  },
  irregularidades: {
    key: 'irregularidades',
    label: 'Irregularidades',
    singular: 'irregularidade',
    description: 'Códigos, descrições e valores de irregularidades.',
    fields: [
      { key: 'codigo', label: 'Código', type: 'integer', required: true },
      { key: 'descricao', label: 'Descrição', type: 'text', required: true, multiline: true },
      { key: 'valor', label: 'Valor', type: 'numeric', required: true },
    ],
    tableColumns: ['codigo', 'descricao', 'valor'],
    matchFields: ['codigo'],
    orderBy: 'codigo',
    sample: {
      codigo: '9999',
      descricao: 'Irregularidade de exemplo',
      valor: '0,00',
    },
  },
}

function nextPatchVersion(version: string) {
  const parts = version.split('.').map((part) => Number.parseInt(part, 10))
  if (parts.length >= 3 && parts.every(Number.isFinite)) return `${parts[0]}.${parts[1]}.${parts[2] + 1}`
  return '8.0.1'
}

function normalizeHeader(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .replace(/ª|º/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function normalizeKeyPart(value: unknown) {
  return String(value ?? '').trim().toLocaleLowerCase('pt-BR')
}

function matchKey(config: DatasetConfig, data: Record<string, unknown>) {
  return config.matchFields.map((field) => normalizeKeyPart(data[field])).join('||')
}

function displayValue(value: unknown) {
  if (value == null || value === '') return '—'
  if (Array.isArray(value)) return value.join(' | ') || '—'
  return String(value)
}

function emptyForm(config: DatasetConfig): FormState {
  return Object.fromEntries(config.fields.map((field) => [field.key, '']))
}

function formFromRecord(config: DatasetConfig, record: DataRecord): FormState {
  return Object.fromEntries(config.fields.map((field) => {
    const value = record[field.key]
    if (field.type === 'array') return [field.key, Array.isArray(value) ? value.join(' | ') : '']
    return [field.key, value == null ? '' : String(value)]
  }))
}

function splitArray(value: string) {
  return value
    .split('|')
    .map((item) => item.trim())
    .filter(Boolean)
}

function parseNumeric(value: string, integer: boolean) {
  const normalized = value.trim().replace(/\./g, '').replace(',', '.')
  if (!normalized) return null
  const number = integer ? Number.parseInt(normalized, 10) : Number(normalized)
  if (!Number.isFinite(number)) throw new Error(`Valor numérico inválido: “${value}”.`)
  return number
}

function formToData(config: DatasetConfig, form: FormState) {
  const data: Record<string, unknown> = {}
  for (const field of config.fields) {
    const raw = String(form[field.key] ?? '').trim()
    if (field.required && !raw) throw new Error(`Informe “${field.label}”.`)

    if (field.type === 'array') data[field.key] = splitArray(raw)
    else if (field.type === 'integer') data[field.key] = parseNumeric(raw, true)
    else if (field.type === 'numeric') data[field.key] = parseNumeric(raw, false)
    else data[field.key] = raw || null
  }
  return data
}

function parseDelimitedText(text: string, delimiter: string) {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]
    const next = text[index + 1]
    if (char === '"') {
      if (quoted && next === '"') {
        cell += '"'
        index += 1
      } else quoted = !quoted
      continue
    }
    if (char === delimiter && !quoted) {
      row.push(cell)
      cell = ''
      continue
    }
    if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && next === '\n') index += 1
      row.push(cell)
      if (row.some((value) => value.trim())) rows.push(row)
      row = []
      cell = ''
      continue
    }
    cell += char
  }

  row.push(cell)
  if (row.some((value) => value.trim())) rows.push(row)
  return rows
}

function parseBulkCsv(config: DatasetConfig, text: string): FormState[] {
  const clean = text.replace(/^\uFEFF/, '').trim()
  if (!clean) throw new Error('O arquivo CSV está vazio.')

  const firstLine = clean.split(/\r?\n/, 1)[0]
  const delimiter = (firstLine.match(/;/g)?.length ?? 0) >= (firstLine.match(/,/g)?.length ?? 0) ? ';' : ','
  const matrix = parseDelimitedText(clean, delimiter)
  if (matrix.length < 2) throw new Error('O CSV precisa ter cabeçalho e pelo menos um registro.')

  const aliases = new Map<string, string>()
  config.fields.forEach((field) => {
    aliases.set(normalizeHeader(field.key), field.key)
    aliases.set(normalizeHeader(field.label), field.key)
  })
  const mapped = matrix[0].map((header) => aliases.get(normalizeHeader(header)) ?? null)

  for (const required of config.fields.filter((field) => field.required)) {
    if (!mapped.includes(required.key)) throw new Error(`O CSV precisa conter a coluna “${required.key}”.`)
  }

  const seen = new Set<string>()
  return matrix.slice(1).map((cells, index) => {
    const form = emptyForm(config)
    mapped.forEach((field, cellIndex) => {
      if (field) form[field] = cells[cellIndex]?.trim() ?? ''
    })
    const data = formToData(config, form)
    const key = matchKey(config, data)
    if (!key.replaceAll('|', '')) throw new Error(`Linha ${index + 2}: chave operacional não informada.`)
    if (seen.has(key)) throw new Error(`Linha ${index + 2}: registro duplicado no CSV.`)
    seen.add(key)
    return form
  })
}

async function upsertInChunks(rows: Record<string, unknown>[], chunkSize = 60) {
  for (let index = 0; index < rows.length; index += chunkSize) {
    const result = await supabase
      .from('draft_changes')
      .upsert(rows.slice(index, index + chunkSize), { onConflict: 'release_id,dataset,record_key' })
    if (result.error) throw result.error
  }
}

function csvEscape(value: string) {
  if (!/[;"\r\n]/.test(value)) return value
  return `"${value.replaceAll('"', '""')}"`
}

function statusLabel(status: DraftChange['approval_status']) {
  if (status === 'aguardando_aprovacao') return 'Aguardando aprovação'
  if (status === 'aprovado') return 'Aprovado'
  if (status === 'rejeitado') return 'Rejeitado'
  return 'Rascunho'
}

export function StandardDatasetEditor({ datasetKey, onBack }: { datasetKey: StandardDatasetKey; onBack: () => void }) {
  const config = CONFIGS[datasetKey]
  const { profile } = useAuth()
  const [records, setRecords] = useState<DataRecord[]>([])
  const [draftRelease, setDraftRelease] = useState<DraftRelease | null>(null)
  const [drafts, setDrafts] = useState<DraftChange[]>([])
  const [search, setSearch] = useState('')
  const [form, setForm] = useState<FormState>(() => emptyForm(config))
  const [editing, setEditing] = useState<DataRecord | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkText, setBulkText] = useState('')
  const [bulkFileName, setBulkFileName] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  async function load() {
    setLoading(true)
    setError('')
    try {
      const selectFields = ['id', ...config.fields.map((field) => field.key)].join(',')
      const [recordsResult, releaseResult] = await Promise.all([
        supabase.from(config.key).select(selectFields).order(config.orderBy),
        supabase
          .from('releases')
          .select('id,version,titulo,status')
          .eq('status', 'rascunho')
          .order('id', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ])
      if (recordsResult.error) throw recordsResult.error
      if (releaseResult.error) throw releaseResult.error

      setRecords((recordsResult.data ?? []) as unknown as DataRecord[])
      const release = (releaseResult.data as DraftRelease | null) ?? null
      setDraftRelease(release)

      if (!release) {
        setDrafts([])
        return
      }

      const draftResult = await supabase
        .from('draft_changes')
        .select('id,release_id,operacao,record_key,dados_anteriores,dados_novos,approval_status,rejection_reason')
        .eq('release_id', release.id)
        .eq('dataset', config.key)
        .order('id')
      if (draftResult.error) throw draftResult.error
      setDrafts((draftResult.data ?? []) as DraftChange[])
    } catch (err) {
      setError(err instanceof Error ? err.message : `Não foi possível carregar ${config.label}.`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [datasetKey])

  const officialByMatch = useMemo(() => {
    return new Map(records.map((record) => [matchKey(config, record), record]))
  }, [records, config])

  const pendingById = useMemo(() => {
    const map = new Map<number, DraftChange>()
    drafts.forEach((draft) => {
      if (draft.record_key.startsWith('id:')) {
        const id = Number(draft.record_key.slice(3))
        if (Number.isFinite(id)) map.set(id, draft)
      }
    })
    return map
  }, [drafts])

  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('pt-BR')
    if (!term) return records
    return records.filter((record) =>
      config.fields.some((field) => displayValue(record[field.key]).toLocaleLowerCase('pt-BR').includes(term)),
    )
  }, [records, search, config])

  const allApproved = drafts.length > 0 && drafts.every((draft) => draft.approval_status === 'aprovado')

  async function ensureDraftRelease() {
    if (draftRelease) return draftRelease
    if (!profile) throw new Error('Perfil não carregado.')

    const latest = await supabase.from('releases').select('version').order('id', { ascending: false }).limit(1).maybeSingle()
    if (latest.error) throw latest.error
    const version = nextPatchVersion(latest.data?.version ?? '8.0.0')
    const created = await supabase
      .from('releases')
      .insert({
        version,
        titulo: `Atualização Suite ${version}`,
        resumo: 'Alterações preparadas pelo painel administrativo.',
        status: 'rascunho',
        created_by: profile.id,
      })
      .select('id,version,titulo,status')
      .single()
    if (created.error) throw created.error
    const release = created.data as DraftRelease
    setDraftRelease(release)
    return release
  }

  function openNew() {
    setEditing(null)
    setForm(emptyForm(config))
    setFormOpen(true)
    setBulkOpen(false)
    setError('')
    setMessage('')
  }

  function openEdit(record: DataRecord) {
    setEditing(record)
    setForm(formFromRecord(config, record))
    setFormOpen(true)
    setBulkOpen(false)
    setError('')
    setMessage('')
  }

  function closeForm() {
    setEditing(null)
    setForm(emptyForm(config))
    setFormOpen(false)
  }

  async function saveDraft(event: FormEvent) {
    event.preventDefault()
    if (!profile) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const data = formToData(config, form)
      if (!editing && officialByMatch.has(matchKey(config, data))) {
        throw new Error('Já existe um registro com essa chave operacional. Use Editar na base oficial.')
      }
      const release = await ensureDraftRelease()
      const recordKey = editing ? `id:${editing.id}` : `new:${config.key}:${matchKey(config, data)}`
      const result = await supabase.from('draft_changes').upsert({
        release_id: release.id,
        dataset: config.key,
        operacao: editing ? 'UPDATE' : 'INSERT',
        record_key: recordKey,
        dados_anteriores: editing ? Object.fromEntries(config.fields.map((field) => [field.key, editing[field.key]])) : null,
        dados_novos: data,
        created_by: profile.id,
        updated_by: profile.id,
      }, { onConflict: 'release_id,dataset,record_key' })
      if (result.error) throw result.error
      closeForm()
      setMessage(`Alteração salva no rascunho ${release.version}.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar o rascunho.')
    } finally {
      setSaving(false)
    }
  }

  async function markDelete(record: DataRecord) {
    if (!profile) return
    if (!window.confirm(`Preparar exclusão deste ${config.singular} para a próxima publicação?`)) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const release = await ensureDraftRelease()
      const result = await supabase.from('draft_changes').upsert({
        release_id: release.id,
        dataset: config.key,
        operacao: 'DELETE',
        record_key: `id:${record.id}`,
        dados_anteriores: Object.fromEntries(config.fields.map((field) => [field.key, record[field.key]])),
        dados_novos: null,
        created_by: profile.id,
        updated_by: profile.id,
      }, { onConflict: 'release_id,dataset,record_key' })
      if (result.error) throw result.error
      setMessage(`Exclusão preparada no rascunho ${release.version}.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível preparar a exclusão.')
    } finally {
      setSaving(false)
    }
  }

  async function markDeleteAll() {
    if (!profile || records.length === 0) return
    const confirmation = window.prompt(
      `Preparar a exclusão dos ${records.length} registros de ${config.label}? Nada será apagado antes da publicação.\n\nDigite EXCLUIR ${records.length} para continuar.`,
    )
    if (confirmation !== `EXCLUIR ${records.length}`) return

    setSaving(true)
    setError('')
    setMessage('')
    try {
      const release = await ensureDraftRelease()
      await upsertInChunks(records.map((record) => ({
        release_id: release.id,
        dataset: config.key,
        operacao: 'DELETE',
        record_key: `id:${record.id}`,
        dados_anteriores: Object.fromEntries(config.fields.map((field) => [field.key, record[field.key]])),
        dados_novos: null,
        created_by: profile.id,
        updated_by: profile.id,
      })))
      setMessage(`${records.length} exclusões preparadas no rascunho ${release.version}.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível preparar a exclusão total.')
    } finally {
      setSaving(false)
    }
  }

  function downloadCsvTemplate() {
    const headers = config.fields.map((field) => field.key)
    const row = headers.map((key) => csvEscape(config.sample[key] ?? '')).join(';')
    const blob = new Blob([`\uFEFF${headers.join(';')}\n${row}\n`], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `modelo_${config.key}_suite.csv`
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
  }

  async function readBulkFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return
    try {
      setBulkText(await file.text())
      setBulkFileName(file.name)
      setError('')
      setMessage(`Arquivo “${file.name}” carregado.`)
    } catch {
      setError('Não foi possível ler o arquivo selecionado.')
    }
  }

  async function prepareBulkImport() {
    if (!profile) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const rows = parseBulkCsv(config, bulkText)
      const release = await ensureDraftRelease()
      const changes = rows.map((form) => {
        const data = formToData(config, form)
        const official = officialByMatch.get(matchKey(config, data))
        return {
          release_id: release.id,
          dataset: config.key,
          operacao: official ? 'UPDATE' : 'INSERT',
          record_key: official ? `id:${official.id}` : `new:${config.key}:${matchKey(config, data)}`,
          dados_anteriores: official ? Object.fromEntries(config.fields.map((field) => [field.key, official[field.key]])) : null,
          dados_novos: data,
          created_by: profile.id,
          updated_by: profile.id,
        }
      })
      await upsertInChunks(changes)
      setBulkOpen(false)
      setBulkText('')
      setBulkFileName('')
      setMessage(`${rows.length} registro(s) preparado(s) em massa no rascunho ${release.version}.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível preparar a importação em massa.')
    } finally {
      setSaving(false)
    }
  }

  async function discardDraft(draft: DraftChange) {
    if (!window.confirm('Descartar esta alteração pendente?')) return
    setSaving(true)
    setError('')
    try {
      const result = await supabase.from('draft_changes').delete().eq('id', draft.id)
      if (result.error) throw result.error
      setMessage('Alteração descartada.')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível descartar a alteração.')
    } finally {
      setSaving(false)
    }
  }

  async function publish() {
    if (!draftRelease || profile?.role !== 'administrador' || !allApproved) return
    if (!window.confirm(`Publicar ${drafts.length} alteração(ões) como versão ${draftRelease.version}?`)) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const result = await supabase.rpc('publish_release', { target_release_id: draftRelease.id })
      if (result.error) throw result.error
      setMessage(`Versão ${draftRelease.version} publicada com sucesso.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível publicar a atualização.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="workspace-content admin-editor">
      <div className="page-heading">
        <div>
          <button className="text-button" onClick={onBack}>← Voltar ao painel</button>
          <p className="eyebrow">ADMINISTRAÇÃO · {config.label.toLocaleUpperCase('pt-BR')}</p>
          <h1>Editor de {config.label}</h1>
          <p className="muted">{config.description} Nada chega à operação antes da aprovação e publicação.</p>
        </div>
        <div className="heading-actions">
          {draftRelease && <span className="status-chip warning">Rascunho {draftRelease.version}</span>}
          <button className="secondary-button compact" disabled={saving} onClick={() => { setBulkOpen((current) => !current); setFormOpen(false) }}>Importar em massa</button>
          <button className="primary-button compact" disabled={saving} onClick={openNew}>+ Novo registro</button>
        </div>
      </div>

      {message && <div className="notice success">{message}</div>}
      {error && <div className="notice error">{error}</div>}

      <div className="editor-summary">
        <article><span>Base oficial</span><strong>{records.length}</strong><small>registros publicados</small></article>
        <article><span>Alterações pendentes</span><strong>{drafts.length}</strong><small>{draftRelease?.version ?? 'sem rascunho ativo'}</small></article>
        <article><span>Publicação</span><strong>{profile?.role === 'administrador' ? (allApproved || drafts.length === 0 ? 'Liberada' : 'Aguardando aprovação') : 'Somente Admin'}</strong><small>Gestão prepara; Admin aprova e publica</small></article>
      </div>

      <section className="bulk-operations-bar">
        <div>
          <strong>Operações em massa</strong>
          <span>Você pode substituir toda a base preparando exclusões e importando o novo CSV na mesma release.</span>
        </div>
        <div className="bulk-operations-actions">
          <button className="secondary-button compact" onClick={downloadCsvTemplate}>Baixar modelo CSV</button>
          <button className="danger-button compact" disabled={saving || records.length === 0} onClick={() => void markDeleteAll()}>Excluir toda a base</button>
        </div>
      </section>

      {bulkOpen && (
        <section className="editor-form-card bulk-import-card">
          <div className="pending-head">
            <div><p className="eyebrow">IMPORTAÇÃO EM MASSA</p><h2>Preparar vários registros</h2></div>
            <button className="text-button" onClick={() => setBulkOpen(false)}>Fechar</button>
          </div>
          <div className="bulk-file-row">
            <label className="file-picker">Selecionar CSV<input type="file" accept=".csv,text/csv" onChange={(event) => void readBulkFile(event)} /></label>
            <span>{bulkFileName || 'Nenhum arquivo selecionado'}</span>
          </div>
          <textarea className="bulk-textarea" value={bulkText} onChange={(event) => setBulkText(event.target.value)} rows={10} placeholder="Você também pode colar o CSV aqui..." />
          <div className="notice info compact-note">Campos do tipo lista usam | para separar os itens. Registros existentes são identificados pela chave operacional da base e viram UPDATE.</div>
          <div className="form-actions">
            <button className="secondary-button" type="button" onClick={downloadCsvTemplate}>Baixar modelo</button>
            <button className="primary-button compact" type="button" disabled={saving || !bulkText.trim()} onClick={() => void prepareBulkImport()}>{saving ? 'Preparando...' : 'Preparar importação'}</button>
          </div>
        </section>
      )}

      {drafts.length > 0 && (
        <section className="pending-panel">
          <div className="pending-head">
            <div><p className="eyebrow">RASCUNHO</p><h2>Alterações da base</h2></div>
            <button className="primary-button compact" disabled={saving || profile?.role !== 'administrador' || !allApproved} onClick={() => void publish()}>
              {allApproved ? `Publicar ${draftRelease?.version}` : 'Aguardando aprovação'}
            </button>
          </div>
          <div className="pending-list">
            {drafts.map((draft) => (
              <div className="pending-item" key={draft.id}>
                <span className={`operation-badge ${draft.operacao.toLowerCase()}`}>{draft.operacao}</span>
                <div>
                  <strong>{draft.record_key.startsWith('id:') ? `Registro #${draft.record_key.slice(3)}` : displayValue(config.matchFields.map((field) => draft.dados_novos?.[field]).join(' · '))}</strong>
                  <small>{statusLabel(draft.approval_status)}{draft.rejection_reason ? ` · ${draft.rejection_reason}` : ''}</small>
                </div>
                <button className="danger-link" disabled={saving || draft.approval_status === 'aguardando_aprovacao'} onClick={() => void discardDraft(draft)}>Descartar</button>
              </div>
            ))}
          </div>
        </section>
      )}

      {formOpen && (
        <section className="editor-form-card">
          <div className="pending-head">
            <div><p className="eyebrow">{editing ? 'EDITAR' : 'NOVO REGISTRO'}</p><h2>{editing ? `Registro #${editing.id}` : `Cadastrar ${config.singular}`}</h2></div>
            <button className="text-button" onClick={closeForm}>Fechar</button>
          </div>
          <form className="editor-form" onSubmit={saveDraft}>
            {config.fields.map((field) => (
              <label key={field.key} className={field.multiline ? 'span-2' : undefined}>
                {field.label}
                {field.multiline ? (
                  <textarea rows={field.type === 'array' ? 4 : 5} value={form[field.key] ?? ''} placeholder={field.placeholder} onChange={(event) => setForm((current) => ({ ...current, [field.key]: event.target.value }))} required={field.required} />
                ) : (
                  <input type={field.type === 'integer' || field.type === 'numeric' ? 'text' : 'text'} value={form[field.key] ?? ''} placeholder={field.placeholder} onChange={(event) => setForm((current) => ({ ...current, [field.key]: event.target.value }))} required={field.required} />
                )}
              </label>
            ))}
            <div className="form-actions span-2">
              <button type="button" className="secondary-button" onClick={closeForm}>Cancelar</button>
              <button type="submit" className="primary-button compact" disabled={saving}>{saving ? 'Salvando...' : 'Salvar em rascunho'}</button>
            </div>
          </form>
        </section>
      )}

      <div className="admin-table-toolbar">
        <div><h2>Base oficial</h2><p className="muted">{filtered.length} de {records.length} registros</p></div>
        <input placeholder={`Pesquisar em ${config.label}...`} value={search} onChange={(event) => setSearch(event.target.value)} />
      </div>

      {loading ? <div className="empty-state">Carregando {config.label}...</div> : (
        <div className="admin-table-wrap">
          <table className="admin-data-table" style={{ minWidth: Math.max(900, config.tableColumns.length * 210) }}>
            <thead><tr>{config.tableColumns.map((field) => <th key={field}>{config.fields.find((item) => item.key === field)?.label ?? field}</th>)}<th>Ações</th></tr></thead>
            <tbody>
              {filtered.map((record) => {
                const pending = pendingById.get(record.id)
                return (
                  <tr key={record.id} className={pending ? 'has-pending' : ''}>
                    {config.tableColumns.map((field, index) => (
                      <td key={field}>{index === 0 ? <strong>{displayValue(record[field])}</strong> : displayValue(record[field])}{index === 0 && pending && <span className="row-pending">{pending.operacao} pendente</span>}</td>
                    ))}
                    <td><div className="table-actions"><button onClick={() => openEdit(record)}>Editar</button><button className="danger-link" onClick={() => void markDelete(record)}>Excluir</button></div></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
